import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
} from '@mui/material';
import { collection, getDocs, addDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../UserContext';

export default function MemberJoinPennantDialog({ open, onClose, onPennantJoined }) {
  const { user, memberData } = useUser();
  const [allPennants, setAllPennants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPennant, setSelectedPennant] = useState(null);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [requestedSection, setRequestedSection] = useState('Choose for me');

  useEffect(() => {
    if (!open) return;

    const fetchPennants = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'Pennants'));
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllPennants(docs);
      } catch (error) {
        console.error('Error fetching pennants:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPennants();
  }, [open]);

  const existingPennantIds = new Set();
  if (memberData?.pennants?.length) {
    memberData.pennants.forEach((p) => {
      if (p.pennantId) {
        existingPennantIds.add(p.pennantId);
      } else if (p.id) {
        existingPennantIds.add(p.id);
      }
    });
  }

  const availablePennants = allPennants.filter((p) => !existingPennantIds.has(p.id));

  const handleFetchSections = async (pennant) => {
    setLoadingSections(true);
    try {
      const membersObj = pennant.members || {};
      const sectionNames = Object.keys(membersObj);

      setSections(sectionNames);
      setRequestedSection('Choose for me');

      setSelectedPennant(pennant);
    } catch (error) {
      console.error('Error fetching sections:', error);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleRequestJoin = async () => {
    if (!user || !selectedPennant) return;

    try {
      const requestsCollection = collection(db, 'Pennants', selectedPennant.id, 'requestsToJoin');
      await addDoc(requestsCollection, {
        memberId: user.squareCustomerId,
        memberName: `${memberData.firstName} ${memberData.lastName}`,
        email: memberData.email,
        requestedAt: new Date(),
        status: 'pending',
        requestedSection,
        sport: selectedPennant.sport || '',
      });

      alert(
        `Join request sent for pennant "${
          selectedPennant.sport ? `${selectedPennant.sport} ` : ''
        }${selectedPennant.name || selectedPennant.id}" (Section: ${requestedSection}).`
      );

      if (onPennantJoined) onPennantJoined();

      onClose();
    } catch (error) {
      console.error('Error sending join request:', error);
      alert('Failed to send join request. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Join a Pennant</DialogTitle>
      <DialogContent dividers>
        {loading && <Typography>Loading available pennants...</Typography>}
        {!loading && availablePennants.length === 0 && (
          <Typography>You're already in all existing pennants, or none are available.</Typography>
        )}

        <List>
          {availablePennants.map((pennant) => {
            const displayName = `${
              pennant.sport ? `${pennant.sport} ` : ''
            }${pennant.name || `Pennant ${pennant.id}`}`.trim();

            return (
              <ListItem
                key={pennant.id}
                button
                onClick={() => handleFetchSections(pennant)}
              >
                <ListItemText
                  primary={displayName}
                  secondary={pennant.description || 'No description provided.'}
                />
              </ListItem>
            );
          })}
        </List>

        {selectedPennant && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd' }}>
            <Typography variant="subtitle1" gutterBottom>
              You have chosen:{' '}
              {selectedPennant.sport
                ? `${selectedPennant.sport} ${selectedPennant.name}`
                : selectedPennant.name || selectedPennant.id}
            </Typography>
            {loadingSections ? (
              <CircularProgress size={24} />
            ) : (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="section-select-label">Choose Section</InputLabel>
                  <Select
                    labelId="section-select-label"
                    value={requestedSection}
                    label="Choose Section"
                    onChange={(e) => setRequestedSection(e.target.value)}
                  >
                    <MenuItem value="Choose for me">Choose for me</MenuItem>
                    {sections.map((sectionName) => (
                      <MenuItem key={sectionName} value={sectionName}>
                        {sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleRequestJoin}
                  disabled={!selectedPennant}
                >
                  Request to Join
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MemberJoinPennantDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPennantJoined: PropTypes.func,
};
